import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { CoachTopBar, useCoachRefresh } from './CoachShell';
import AppointmentSheet from './AppointmentSheet';
import TargetsSheet, { savedText } from './TargetsSheet';
import { OfflineTag } from './OfflineSheets';
import ShareScheduleSheet from './ShareScheduleSheet';
import { NewBadge, useAnnouncementAnchor } from '../components/Announcements';
import { addDays, longDate, parseISODate, today, weekDays } from '../lib/dates';
import { absenceLabel, clock, localDate, weekLabel, weekQuery, weekdayShort } from '../lib/schedule';
import { kcal } from '../lib/format';
import { useApp } from '../lib/AppContext';

const PARTS = [{ id: 'targets', label: 'Mục tiêu' }, { id: 'schedule', label: 'Lịch tập' }];
const TAG = 'rounded-full px-2 py-0.5 text-[10px] font-bold';
const SOURCES = {
  coach: ['Coach đặt', 'bg-[#e3f1e9] text-[#17804f]'],
  changed: ['Đã đổi mục tiêu bạn đặt', 'bg-[#fdf3d7] text-[#8a6100]'],
  self: ['Tự đặt', 'bg-base-200 text-muted'],
};

// Kế hoạch → Mục tiêu: each student's calories and protein and who set them, with "Đặt mục tiêu".
function TargetsPart({ students, onEdit }) {
  if (!students) return <div className="skeleton h-40 rounded-[20px]" />;
  if (students.length === 0) return <p className="rounded-[20px] bg-base-100 p-5 text-sm text-muted">Chưa có học viên nào đang kết nối.</p>;
  return (
    <>
      <ul className="flex flex-col gap-2">
        {students.map((s) => {
          const [label, color] = SOURCES[s.source] || SOURCES.self;
          return (
            <li key={s.client_id} className="flex items-center gap-3 rounded-[18px] bg-base-100 p-3">
              <Avatar name={s.name} url={s.avatar_url} size={40} />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-1.5 font-bold"><span className="truncate">{s.name}</span><span className={`${TAG} ${color}`}>{label}</span></p>
                <p className="text-xs text-muted">
                  {s.calorie_goal ? `${kcal(s.calorie_goal)} kcal` : 'Chưa có mục tiêu'}
                  {s.protein_goal ? ` · ${s.protein_goal} g đạm` : ''}
                  {s.workout_protein_goal ? ` · ngày tập ${s.workout_protein_goal} g` : ''}
                </p>
              </div>
              <button type="button" className="btn btn-sm h-9 shrink-0 rounded-full border-0 bg-[#e3f1e9] px-3 text-[#17804f]" onClick={() => onEdit(s)}>
                Đặt mục tiêu
              </button>
            </li>
          );
        })}
      </ul>
      <p className="px-1 text-xs text-muted">Học viên tự lên thực đơn theo mục tiêu bạn đặt và vẫn đổi được; bạn sẽ được báo khi họ đổi.</p>
    </>
  );
}

// A past session of someone without the app that the coach can still mark (from its start, for a week).
const unmarked = (a) => a.offline && a.can_mark && !a.attendance;

// One session on the day's list. For someone without the app (`a.offline`): a dashed avatar, "Chưa dùng app",
// and once it has started, Đã tập / Vắng for the coach to mark (changeable for a week).
function AppointmentRow({ a, onOpen, onMark }) {
  const absent = a.state === 'absent';
  const past = a.state === 'past';
  const started = a.offline && a.can_mark;
  return (
    <li className="flex gap-3">
      <div className="w-12 shrink-0 pt-3 text-right">
        <p className={`tabular font-bold ${absent ? 'text-muted line-through' : ''}`}>{clock(a.starts_at)}</p>
        <p className="text-[11px] text-muted">{a.duration_min}′</p>
      </div>
      <div className={`flex min-w-0 flex-1 flex-col gap-2 rounded-[16px] border-l-4 bg-base-100 p-3 ${absent ? 'border-[#e8743b] opacity-70' : past ? 'border-line' : 'border-[#17804f]'}`}>
      <button type="button" onClick={() => onOpen(a)} disabled={past} className="flex min-w-0 items-center gap-3 text-left">
        <Avatar name={a.client_name} url={a.client_avatar_url} dashed={!!a.offline} size={36} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate font-bold">{a.client_name}</span>
            {a.offline && <OfflineTag />}
            {absent && <span className={`${TAG} bg-[#fdebe4] text-[#8e3414]`}>Báo nghỉ</span>}
            {!absent && a.repeats && <span className={`${TAG} bg-[#e8eefb] text-[#2f5fb3]`}>Lặp hằng tuần</span>}
            {(past || started) && (a.trained
              ? <span className={`${TAG} bg-[#e3f1e9] text-[#17804f]`}>Đã tập</span>
              : a.attendance === 'missed'
                ? <span className={`${TAG} bg-[#fdebe4] text-[#8e3414]`}>{a.offline ? 'Vắng' : 'Không tập'}</span>
                : !started && <span className={`${TAG} bg-base-200 text-muted`}>Đã qua</span>)}
            {!absent && a.proposal?.status === 'pending' && <span className={`${TAG} bg-[#fdf3d7] text-[#8a6100]`}>Xin đổi {clock(a.proposal.starts_at)}</span>}
          </span>
          {a.note && <span className="block text-xs font-semibold text-[#17804f]">{a.note}</span>}
          {a.location && <span className="flex items-center gap-1 text-xs text-muted"><Icon name="target" className="size-3" />{a.location}</span>}
          {absent && a.absence.reason && <span className="block text-xs italic text-[#8e3414]">“{absenceLabel(a.absence.reason)}”</span>}
        </span>
      </button>
      {started && (
        <div role="radiogroup" aria-label={`Chuyên cần của ${a.client_name}`} className="grid grid-cols-2 gap-2">
          {[['trained', 'Đã tập'], ['missed', 'Vắng']].map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={a.attendance === value} onClick={() => onMark(a, value)}
              className={`h-10 rounded-[12px] text-sm font-bold ${a.attendance === value ? 'bg-[#17804f] text-white' : 'border border-line bg-base-100'}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      </div>
    </li>
  );
}

// Kế hoạch → Lịch tập (A3): the week's days with a dot per appointment, and the chosen day's appointments of
// all students in time order. Tapping one edits it; "+ Thêm lịch" adds one.
function SchedulePart({ data, day, setDay, week, setWeek, onOpen, onMark }) {
  const days = weekDays(week);
  const list = (data?.appointments || []).filter((a) => localDate(a.starts_at) === day);
  // Days with sessions of people without the app still to mark: a dot, no push.
  const toMark = new Set((data?.appointments || []).filter(unmarked).map((a) => localDate(a.starts_at)));
  const absent = list.filter((a) => a.state === 'absent').length;
  return (
    <>
      <div className="flex items-center justify-between rounded-[16px] bg-base-100 px-1 py-1">
        <button type="button" className="btn btn-circle btn-ghost size-11" aria-label="Tuần trước" onClick={() => setWeek(addDays(week, -7))}>
          <Icon name="chevronLeft" className="size-5" />
        </button>
        <span className="text-sm font-bold">{weekLabel(week)}</span>
        <button type="button" className="btn btn-circle btn-ghost size-11" aria-label="Tuần sau" onClick={() => setWeek(addDays(week, 7))}>
          <Icon name="chevronRight" className="size-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1" role="tablist" aria-label="Ngày trong tuần">
        {days.map((d) => {
          const n = data?.counts?.[d] || 0;
          const active = d === day;
          return (
            <button key={d} type="button" role="tab" aria-selected={active} onClick={() => setDay(d)}
              aria-label={`${longDate(d)}, ${n} buổi`}
              className={`flex min-h-16 flex-col items-center justify-center gap-0.5 rounded-[14px] ${active ? 'bg-[#15261f] text-white' : 'bg-base-100'}`}>
              <span className={`text-[10px] font-semibold ${active ? 'text-[#a9b8b0]' : 'text-muted'}`}>{weekdayShort(d)}</span>
              <span className="tabular relative text-base font-bold">
                {parseISODate(d).getDate()}
                {toMark.has(d) && <span className="absolute -right-2 top-0 size-2 rounded-full bg-[#e8743b]"><span className="sr-only">, có buổi chưa đánh dấu</span></span>}
              </span>
              <span className="flex h-1.5 gap-0.5">
                {Array.from({ length: Math.min(n, 3) }, (_, i) => <span key={i} className={`size-1.5 rounded-full ${active ? 'bg-[#6fd3a0]' : 'bg-[#17804f]'}`} />)}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-baseline justify-between px-1 pt-1">
        <h2 className="font-bold">{longDate(day)}</h2>
        <span className="text-xs text-muted">{list.length} buổi{absent ? ` · ${absent} báo nghỉ` : ''}</span>
      </div>
      {!data && <div className="skeleton h-24 rounded-[16px]" />}
      {data && list.length === 0 && <p className="rounded-[16px] bg-base-100 p-4 text-sm text-muted">Không có lịch hẹn ngày này.</p>}
      {list.length > 0 && <ul className="flex flex-col gap-2">{list.map((a) => <AppointmentRow key={a.id} a={a} onOpen={onOpen} onMark={onMark} />)}</ul>}
      {list.some((a) => a.offline)
        ? <p className="px-1 text-xs text-muted">Người chưa dùng app không nhận thông báo. Buổi đã qua, bạn tự đánh dấu Đã tập hoặc Vắng để theo dõi chuyên cần.</p>
        : <p className="px-1 text-xs text-muted">Giờ hẹn do bạn đặt sau khi thống nhất với học viên. Học viên xem lịch, được nhắc trước, có thể báo nghỉ hoặc xin đổi giờ.</p>}
    </>
  );
}

// Kế hoạch: the students' targets (Mục tiêu) and the appointments with them (Lịch tập).
export default function PlanTab() {
  const { api } = useApp();
  const refreshCoach = useCoachRefresh();
  const [params, setParams] = useSearchParams();
  const part = params.get('part') === 'targets' ? 'targets' : 'schedule';
  const [students, setStudents] = useState(null);
  const [day, setDay] = useState(today());
  const [week, setWeek] = useState(today());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sheet, setSheet] = useState(null); // { editing } or { defaults }
  const [targetsFor, setTargetsFor] = useState(null);
  const [toast, setToast] = useState(null);
  const say = (text) => { setToast(text); setTimeout(() => setToast(null), 3500); };
  const addAnchor = useAnnouncementAnchor('coach_schedule_add');
  const shareAnchor = useAnnouncementAnchor('coach_schedule_share');
  const [sharing, setSharing] = useState(false);

  const loadStudents = useCallback(() => api.get('/coach/targets').then((d) => setStudents(d.students)).catch((e) => setError(e.message)), [api]);
  const loadWeek = useCallback(() => api.get(`/coach/appointments?${weekQuery(week)}`).then(setData).catch((e) => setError(e.message)), [api, week]);
  useEffect(() => { loadStudents(); }, [loadStudents]);
  useEffect(() => { setData(null); loadWeek(); }, [loadWeek]);
  // Moving to another week keeps the same weekday selected.
  useEffect(() => {
    const offset = (parseISODate(day).getDay() + 6) % 7;
    const target = addDays(weekDays(week)[0], offset);
    if (target !== day) setDay(target);
  }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Thêm lịch tập" from a student's options opens the sheet for them (?add=<id>), or for someone without the
  // app (?add_offline=<id>).
  useEffect(() => {
    const add = params.get('add');
    const addOffline = params.get('add_offline');
    if (!add && !addOffline) return;
    setSheet({ defaults: { clientId: add, offlineId: addOffline, date: day } });
    const next = new URLSearchParams(params);
    next.delete('add');
    next.delete('add_offline');
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setPart = (id) => setParams(id === 'schedule' ? {} : { part: id }, { replace: true });
  const pickDay = (d) => { setDay(d); };
  const students4sheet = useMemo(() => students || [], [students]);

  return (
    <div className="flex flex-col gap-3">
      <CoachTopBar>
        {part === 'schedule' && (
          <button ref={addAnchor} type="button" className="btn h-11 rounded-full border-0 bg-primary px-4 font-bold text-primary-content"
            onClick={() => setSheet({ defaults: { date: day < today() ? today() : day } })}>
            <Icon name="plus" className="size-5" /> Thêm lịch
          </button>
        )}
      </CoachTopBar>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-[26px] font-bold">Kế hoạch</h1>
        {part === 'schedule' && (
          <button ref={shareAnchor} type="button" className="btn relative h-10 gap-1.5 rounded-full border-line bg-base-100 px-3.5 text-sm font-semibold"
            aria-label="Chia sẻ lịch tuần" onClick={() => setSharing(true)}>
            <Icon name="upload" className="size-4" /> Chia sẻ
            <NewBadge anchor="coach_schedule_share" dot />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1 rounded-[16px] bg-base-300/60 p-1" role="tablist" aria-label="Kế hoạch">
        {PARTS.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={part === p.id} onClick={() => setPart(p.id)}
            className={`h-10 rounded-[12px] text-sm font-semibold ${part === p.id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>{p.label}</button>
        ))}
      </div>
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}

      {part === 'targets'
        ? <TargetsPart students={students} onEdit={setTargetsFor} />
        : <SchedulePart data={data} day={day} setDay={pickDay} week={week} setWeek={setWeek} onOpen={(a) => setSheet({ editing: a })}
          onMark={async (a, attendance) => {
            try {
              await api.post(`/coach/appointments/${a.id}/attendance`, { attendance });
              loadWeek();
            } catch (e) {
              say(e.message);
            }
          }} />}

      <AppointmentSheet open={!!sheet} students={students4sheet} editing={sheet?.editing} defaults={sheet?.defaults} lastLocation={data?.last_location}
        onClose={() => setSheet(null)}
        onSaved={(saved, message) => {
          setSheet(null);
          say(message);
          if (saved) { setWeek(localDate(saved.starts_at)); setDay(localDate(saved.starts_at)); }
          loadWeek();
          refreshCoach();
        }} />
      <ShareScheduleSheet open={sharing} week={week} appointments={data?.appointments} onClose={() => setSharing(false)} />
      <TargetsSheet student={targetsFor} onClose={() => setTargetsFor(null)}
        onSaved={(saved) => { say(savedText(saved)); loadStudents(); }} />
      {toast && <div role="status" className="fixed inset-x-4 bottom-28 z-40 mx-auto max-w-sm rounded-2xl bg-[#17201b] px-4 py-3 text-center text-sm font-semibold text-white">{toast}</div>}
    </div>
  );
}
